/**
 * Выбор слота публикации. 09 §9.4 — нормативно, здесь ничего не додумывается.
 *
 * Свободного ввода даты нет и не будет. Причина не в экономии кода: клиент, который сам
 * собирает момент, породит значение, которое `CHECK (slot_at_ms % 1000 = 0)` и
 * `publication_slot` отвергнут уже после того, как человек нажал «утвердить». Поэтому
 * список приходит с сервера целиком, а наружу уходит `slot_at` выбранной строки ДОСЛОВНО —
 * та же строка байт в байт, что пришла в ответе.
 *
 * Показываются только `state === 'free'` и только будущие слоты. Занятость определяется
 * полем `state`, а НЕ наличием `draft_id`: у `skipped` и `passed` черновика нет, но
 * свободными они не являются.
 *
 * Подпись строки — `slot_local` из ответа плюс метка зоны оттуда же. Пересчитывать момент в
 * зону бренда на клиенте нельзя: зона живёт в конфиге бренда, а у `packages/ui` из `@smm/*`
 * разрешён только `contracts` (правило R4) — то есть единственный законный путь зоны в UI
 * это HTTP-ответ.
 */

import { isoToMs, msToIso, nowMs } from '@smm/contracts';
import { type JSX, useState } from 'react';
import type { ScheduleResponse, ScheduleSlot } from '../../api/types.ts';
import { useResource } from '../../api/useResource.ts';

/** Горизонт выбора — 28 дней (09 §9.4). */
const HORIZON_MS = 28 * 24 * 60 * 60 * 1000;

const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'] as const;
const SAKAMOTO = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4] as const;

/**
 * День недели по гражданской дате (алгоритм Сакамото). Считается ИМЕННО по `slot_local`, а
 * не по `slot_at`: подпись строки обязана быть проекцией одной величины, иначе на переводе
 * часов день недели и время в одной строке разъедутся. `new Date` при этом не нужен вовсе —
 * правило R11 (единственный дом времени) соблюдается без исключений.
 */
export function weekdayOfCivilDate(year: number, month: number, day: number): string {
  const y = month < 3 ? year - 1 : year;
  const shift = SAKAMOTO[month - 1] ?? 0;
  const idx =
    (((y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) + shift + day) % 7) + 7) %
    7;
  return WEEKDAYS[idx] ?? '';
}

const LOCAL_MINUTE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/** 'YYYY-MM-DDTHH:MM' → «вт 25.08 · 12:00». Форма подписи — из макета Approve.html. */
export function slotLabel(slotLocal: string): string {
  const m = LOCAL_MINUTE.exec(slotLocal);
  if (m === null) return slotLocal;
  const [, y, mo, d, hh, mi] = m as unknown as [string, string, string, string, string, string];
  const wd = weekdayOfCivilDate(Number(y), Number(mo), Number(d));
  return `${wd} ${d}.${mo} · ${hh}:${mi}`;
}

export interface SlotPickerProps {
  readonly brand: string;
  readonly selectedSlotId: string | null;
  /** Зона отдаётся вместе со слотом: подписать слот без неё нечем. */
  readonly onPick: (slot: ScheduleSlot, timezone: string) => void;
}

export function SlotPicker({ brand, selectedSlotId, onPick }: SlotPickerProps): JSX.Element {
  // Диапазон считается ОДИН раз за время жизни компонента. Считай мы его на каждом рендере,
  // строка пути менялась бы каждый раз, а она — зависимость эффекта загрузки: получился бы
  // бесконечный цикл запросов.
  const [range] = useState(() => {
    const from = nowMs();
    return { from: msToIso(from), to: msToIso(from + HORIZON_MS) };
  });

  const path = `/api/v1/schedule?brand=${encodeURIComponent(brand)}&from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`;
  const schedule = useResource<ScheduleResponse>(path);

  if (schedule.loading) {
    return <div className="micro">Загружаем расписание…</div>;
  }
  if (schedule.error !== undefined || schedule.data === undefined) {
    return (
      <div className="val val-err">
        <span>Расписание не загрузилось: {schedule.error?.message ?? 'пустой ответ'}</span>
      </div>
    );
  }

  const { timezone, slots } = schedule.data;
  const now = nowMs();
  const free = slots.filter((s) => s.state === 'free' && isoToMs(s.slot_at) > now);

  return (
    <div className="stack" style={{ gap: 8 }}>
      <span className="lbl" style={{ margin: 0 }}>
        Слот публикации — свободные слоты расписания, зона {timezone}
      </span>

      {free.length === 0 ? (
        <div className="val val-warn">
          <span>
            Свободных слотов в ближайшие 28 дней нет. Освободите слот на вкладке «Контент-план» или
            дождитесь следующей недели расписания.
          </span>
        </div>
      ) : (
        <div className="stack" style={{ gap: 8, maxHeight: 260, overflowY: 'auto' }}>
          {free.map((slot) => {
            const on = slot.slot_id === selectedSlotId;
            return (
              <button
                key={slot.slot_id}
                type="button"
                className={on ? 'slot on' : 'slot'}
                aria-pressed={on}
                onClick={() => {
                  onPick(slot, timezone);
                }}
                style={{ width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
              >
                <span className="rad" />
                <span className="sd">{slotLabel(slot.slot_local)}</span>
                <span style={{ flex: 1 }} />
                {slot.ambiguous ? (
                  <span className="meta" style={{ color: 'var(--amber-ink)' }}>
                    местное время повторяется дважды — перевод часов
                  </span>
                ) : (
                  <span className="meta" style={{ color: 'var(--green-ink)' }}>
                    свободен
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
